import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { 
  BadgeCheck, 
  Clock, 
  CheckCircle, 
  XCircle, 
  ExternalLink, 
  Building2, 
  User, 
  FileText, 
  MoreVertical,
  ChevronDown,
  AlertTriangle,
  Mail,
  MessageSquare
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { format } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter, 
  DialogDescription 
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/use-auth";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/reivindicacoes")({
  component: ReivindicacoesAdmin,
});

function ReivindicacoesAdmin() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [currentTab, setCurrentTab] = useState("pendente");
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const { data: reivindicacoes, isLoading } = useQuery({
    queryKey: ["admin-reivindicacoes", currentTab],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reivindicacoes")
        .select(`
          *,
          fornecedores(id, nome, cnpj, cidade, uf, site, instagram, verificado),
          profiles!reivindicacoes_user_id_fkey(nome, email, telefone)
        `)
        .eq("status", currentTab)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data as any[];
    },
  });

  const analyzeMutation = useMutation({
    mutationFn: async ({ id, status, motivo_rejeicao, fornecedor_id, owner_id, verificado }: any) => {
      // 1. Atualizar a reivindicação
      const { data: claimData, error: claimError } = await supabase
        .from("reivindicacoes")
        .update({ 
          status, 
          motivo_rejeicao,
          analisado_por: user?.id,
          analisado_em: new Date().toISOString()
        })
        .eq("id", id)
        .select();
      
      if (claimError) throw claimError;
      if (!claimData || claimData.length === 0) {
        throw new Error("Permissão negada para atualizar reivindicação.");
      }

      // 2. Se aprovada (com ou sem selo), vincular owner
      if (status === 'aprovada') {
        const { data: supplierData, error: supplierError } = await supabase
          .from("fornecedores")
          .update({ 
            owner_id, 
            verificado: !!verificado 
          })
          .eq("id", fornecedor_id)
          .select();
        
        if (supplierError) throw supplierError;
        if (!supplierData || supplierData.length === 0) {
          throw new Error("Permissão negada para atualizar fornecedor.");
        }
      }
      return { success: true };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin-reivindicacoes"] });
      setRejectId(null);
      setRejectReason("");
      toast.success(`Solicitação ${variables.status === 'aprovada' ? 'aprovada' : 'rejeitada'} com sucesso!`);
    },
    onError: (error: any) => {
      toast.error(error.message || "Erro ao processar solicitação.");
    }
  });

  const getComprovanteUrl = async (path: string) => {
    const { data, error } = await supabase.storage
      .from('comprovantes-reivindicacao')
      .createSignedUrl(path, 3600);
    
    if (error) {
      toast.error("Erro ao gerar link do comprovante.");
      return;
    }
    window.open(data.signedUrl, '_blank');
  };

  return (
    <div className="p-4 md:p-8 space-y-6 pb-24 md:pb-8">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Reivindicações de Ficha</h1>
      </div>

      <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
        <TabsList className="bg-white border">
          <TabsTrigger value="pendente" className="gap-2">
            Pendentes
            {reivindicacoes && reivindicacoes.length > 0 && currentTab === 'pendente' && (
              <Badge variant="destructive" className="h-5 px-1.5 min-w-[20px] justify-center">
                {reivindicacoes.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="aprovada">Aprovadas</TabsTrigger>
          <TabsTrigger value="rejeitada">Rejeitadas</TabsTrigger>
        </TabsList>

        <TabsContent value={currentTab} className="mt-6 space-y-6">
          {isLoading ? (
            <div className="text-center py-20 bg-white border rounded-xl">Carregando solicitações...</div>
          ) : !reivindicacoes || reivindicacoes.length === 0 ? (
            <div className="text-center py-20 bg-white border rounded-xl text-muted-foreground">
              Nenhuma solicitação encontrada.
            </div>
          ) : (
            <div className="grid gap-6">
              {reivindicacoes.map((item) => {
                const cnpjDiferente = item.cnpj_informado !== item.fornecedores?.cnpj?.replace(/\D/g, "");
                const emailIgual = item.email === item.profiles?.email;
                
                return (
                  <div key={item.id} className="bg-white border rounded-3xl overflow-hidden shadow-sm">
                    <div className="p-6 grid md:grid-cols-2 gap-8 divide-x-0 md:divide-x">
                      {/* Coluna Esquerda: Dados da Ficha */}
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-2">
                          <Building2 className="w-4 h-4" />
                          <span className="text-xs font-bold uppercase tracking-wider">Dados da Ficha Atual</span>
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">{item.fornecedores?.nome}</h3>
                          <p className="text-sm text-muted-foreground">CNPJ: {item.fornecedores?.cnpj || "Não informado"}</p>
                          <p className="text-sm text-muted-foreground">Local: {item.fornecedores?.cidade}/{item.fornecedores?.uf}</p>
                        </div>
                        <div className="flex gap-4">
                          {item.fornecedores?.site && (
                            <a href={item.fornecedores.site} target="_blank" className="text-xs text-primary font-bold flex items-center gap-1 hover:underline">
                              <ExternalLink className="w-3 h-3" /> Site
                            </a>
                          )}
                          {item.fornecedores?.instagram && (
                            <a href={`https://instagram.com/${item.fornecedores.instagram.replace('@', '')}`} target="_blank" className="text-xs text-primary font-bold flex items-center gap-1 hover:underline">
                              <ExternalLink className="w-3 h-3" /> Instagram
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Coluna Direita: Dados do Solicitante */}
                      <div className="space-y-4 md:pl-8">
                        <div className="flex items-center gap-2 text-muted-foreground mb-2">
                          <User className="w-4 h-4" />
                          <span className="text-xs font-bold uppercase tracking-wider">Dados do Solicitante</span>
                        </div>
                        
                        <div className="space-y-2">
                          <div>
                            <p className="font-bold">{item.nome_solicitante}</p>
                            <div className="flex items-center gap-2">
                              <p className={cn("text-sm", emailIgual ? "text-green-600 font-medium" : "text-muted-foreground")}>
                                {item.email}
                              </p>
                              {emailIgual && <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200">E-mail verificado</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground">{item.telefone}</p>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 bg-secondary/20 p-3 rounded-2xl border">
                            <div>
                              <p className="text-[10px] font-bold uppercase text-muted-foreground">Cargo</p>
                              <p className="text-sm font-medium capitalize">{item.cargo}</p>
                            </div>
                            <div>
                              <p className="text-[10px] font-bold uppercase text-muted-foreground">CNPJ Informado</p>
                              <p className={cn("text-sm font-medium", cnpjDiferente ? "text-red-600" : "text-green-600")}>
                                {item.cnpj_informado}
                              </p>
                            </div>
                          </div>

                          {cnpjDiferente && (
                            <div className="flex items-start gap-2 text-red-600 bg-red-50 p-2 rounded-xl text-[10px] font-medium border border-red-100">
                              <AlertTriangle className="w-3 h-3 shrink-0" />
                              O CNPJ informado é diferente do CNPJ cadastrado na ficha.
                            </div>
                          )}

                          {item.mensagem && (
                            <div className="bg-secondary/10 p-3 rounded-2xl border text-sm italic text-muted-foreground">
                              "{item.mensagem}"
                            </div>
                          )}

                          {item.comprovante_url && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="w-full rounded-xl gap-2"
                              onClick={() => getComprovanteUrl(item.comprovante_url)}
                            >
                              <FileText className="w-4 h-4" /> Ver Comprovante
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Footer com Ações */}
                    <div className="bg-secondary/30 p-4 px-6 border-t flex flex-wrap justify-between items-center gap-4">
                      <div className="text-xs text-muted-foreground">
                        Solicitado em {format(new Date(item.created_at), "dd/MM/yyyy HH:mm")}
                        {item.status !== 'pendente' && (
                          <span className="ml-2">• Analisado em {format(new Date(item.analisado_em), "dd/MM/yyyy")}</span>
                        )}
                      </div>

                      {item.status === 'pendente' && (
                        <div className="flex gap-2">
                          <Button 
                            className="bg-[#C6F764] text-[#0C1A12] hover:bg-[#b5e653] font-bold rounded-xl h-10"
                            onClick={() => {
                              if (confirm("Deseja APROVAR esta reivindicação? Isso concederá o selo de Verificado ao fornecedor.")) {
                                analyzeMutation.mutate({ 
                                  id: item.id, 
                                  status: 'aprovada', 
                                  fornecedor_id: item.fornecedor_id, 
                                  owner_id: item.user_id,
                                  verificado: true
                                });
                              }
                            }}
                          >
                            <BadgeCheck className="w-4 h-4 mr-2" /> Aprovar com Selo
                          </Button>
                          <Button 
                            variant="outline" 
                            className="font-bold rounded-xl h-10"
                            onClick={() => {
                              if (confirm("Deseja APROVAR esta reivindicação SEM o selo de Verificado?")) {
                                analyzeMutation.mutate({ 
                                  id: item.id, 
                                  status: 'aprovada', 
                                  fornecedor_id: item.fornecedor_id, 
                                  owner_id: item.user_id,
                                  verificado: false
                                });
                              }
                            }}
                          >
                            Aprovar sem Selo
                          </Button>
                          <Button 
                            variant="destructive" 
                            className="font-bold rounded-xl h-10"
                            onClick={() => setRejectId(item.id)}
                          >
                            <XCircle className="w-4 h-4 mr-2" /> Rejeitar
                          </Button>
                        </div>
                      )}

                      {item.status === 'rejeitada' && item.motivo_rejeicao && (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 p-2 rounded-xl text-xs">
                          Motivo: {item.motivo_rejeicao}
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!rejectId} onOpenChange={() => setRejectId(null)}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle>Rejeitar Reivindicação</DialogTitle>
            <DialogDescription>
              Informe o motivo da rejeição. O usuário verá esta mensagem.
            </DialogDescription>
          </DialogHeader>
          <Textarea 
            placeholder="Ex: Documentação insuficiente, CNPJ não pertence à empresa..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="rounded-2xl min-h-[120px]"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectId(null)}>Cancelar</Button>
            <Button 
              variant="destructive" 
              onClick={() => analyzeMutation.mutate({ 
                id: rejectId, 
                status: 'rejeitada', 
                motivo_rejeicao: rejectReason 
              })}
              disabled={!rejectReason}
            >
              Confirmar Rejeição
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function cn(...inputs: any[]) {
  return inputs.filter(Boolean).join(" ");
}
